import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useSyncPatientByEhr } from "@elation/api-client/src/stitch-web";
import type { PatientSearchItem } from "@elation/api-client/src/stitch-web/models";

import { navigationV2 } from "@src/shared/navigation/v2";
import { captureException } from "@src/shared/utils/observability/sentry";

import { PatientEntity } from "../../../../../domain";

export function usePatientOpener(navigateStateSource: string) {
  const navigate = useNavigate();
  const syncPatientByEhr = useSyncPatientByEhr();
  const isOpeningPatientRef = useRef(false);
  const [openingKey, setOpeningKey] = useState<string | null>(null);

  const openPatient = useCallback(
    async (patient: PatientSearchItem) => {
      if (isOpeningPatientRef.current || syncPatientByEhr.isPending) return;

      isOpeningPatientRef.current = true;
      setOpeningKey(PatientEntity.searchResultKey(patient));

      try {
        if (patient.patientId != null) {
          navigate(navigationV2.patientDetail(String(patient.patientId)), {
            state: { source: navigateStateSource },
          });
          return;
        }

        if (!patient.ehr || patient.externalPatientId == null) {
          toast.error("Unable to open this patient.");
          return;
        }

        const result = await syncPatientByEhr.mutateAsync({
          ehr: patient.ehr,
          patientId: String(patient.externalPatientId),
        });

        navigate(navigationV2.patientDetail(String(result.data.patient.id)), {
          state: { source: navigateStateSource },
        });
      } catch (error) {
        captureException(error, {
          tags: {
            feature: "patients-v2",
            action: "open-patient-search-result",
          },
          extra: {
            ehr: patient.ehr,
            externalPatientId: patient.externalPatientId,
            patientId: patient.patientId,
          },
        });
        toast.error("Unable to open this patient. Please try again.");
      } finally {
        isOpeningPatientRef.current = false;
        setOpeningKey(null);
      }
    },
    [navigate, navigateStateSource, syncPatientByEhr],
  );

  return {
    openPatient,
    openingKey,
    isOpening: syncPatientByEhr.isPending || openingKey != null,
  } as const;
}
